import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ThumbsUp, ArrowsClockwise, LockKey, Sparkle, Info } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { StrongestCard } from "./StrongestView";
import StockList from "./StockList";

const fmt1 = (n) => (typeof n === "number" ? n.toFixed(1) : "—");

/** Hoverable conviction badge with breakdown popover. */
function ConvictionBadge({ item }) {
  const [open, setOpen] = useState(false);
  const score = item.conviction_score;
  const b = item.conviction_breakdown;
  if (score == null) return null;
  const rows = b
    ? [
        { key: "tech", label: "Tech score", raw: b.tech?.raw != null ? `${fmt1(b.tech.raw)}/10` : "—", earned: b.tech?.earned, max: b.tech?.max },
        { key: "volume", label: "Volume surge", raw: b.volume?.raw != null ? `${fmt1(b.volume.raw)}×` : "—", earned: b.volume?.earned, max: b.volume?.max },
        { key: "adx", label: "ADX (trend)", raw: b.adx?.raw != null ? fmt1(b.adx.raw) : "—", earned: b.adx?.earned, max: b.adx?.max },
        { key: "rs", label: "RS vs Nifty", raw: b.rs?.raw != null ? `${b.rs.raw >= 0 ? "+" : ""}${fmt1(b.rs.raw)}%` : "—", earned: b.rs?.earned, max: b.rs?.max },
        { key: "fresh", label: "Fresh 52w BO", raw: b.fresh?.raw ? "Yes" : "No", earned: b.fresh?.earned, max: b.fresh?.max },
        { key: "sector", label: "Sector strength", raw: b.sector?.raw != null ? `${b.sector.raw >= 0 ? "+" : ""}${fmt1(b.sector.raw)}%` : "—", earned: b.sector?.earned, max: b.sector?.max },
        { key: "delivery", label: "Delivery %", raw: b.delivery?.raw != null ? `${fmt1(b.delivery.raw)}%` : "—", earned: b.delivery?.earned, max: b.delivery?.max },
      ]
    : [];

  return (
    <div
      className="absolute -top-2 left-3 z-20"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`power-conviction-${item.symbol}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos bg-app rounded-sm text-pos shadow-md hover:bg-pos-soft transition flex items-center gap-1"
        data-testid={`power-conviction-btn-${item.symbol}`}
      >
        <span>★ Conviction {score}</span>
        <Info size={9} weight="bold" />
      </button>
      {open && b && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute left-0 top-full mt-1 w-72 p-3 border border-pos/60 bg-surface rounded-sm shadow-xl"
          role="tooltip"
          data-testid={`power-conviction-tooltip-${item.symbol}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-subtle">
            <div className="text-[10px] font-mono uppercase tracking-widest text-secondary-fg">
              Why this pick?
            </div>
            <div className="text-[10px] font-mono tabular-nums">
              <span className="text-pos">{fmt1(b.total)}</span>
              <span className="text-muted-fg"> / 100</span>
            </div>
          </div>
          <div className="space-y-1.5">
            {rows.map((r) => {
              const pct = r.max > 0 ? Math.min(100, (r.earned / r.max) * 100) : 0;
              return (
                <div key={r.key} className="text-[10px] font-mono">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-secondary-fg uppercase tracking-widest">{r.label}</span>
                    <span className="text-muted-fg tabular-nums normal-case">
                      {r.raw} ·{" "}
                      <span className={r.earned >= r.max * 0.7 ? "text-pos" : r.earned > 0 ? "text-mod" : "text-muted-fg"}>
                        +{fmt1(r.earned)}
                      </span>
                      <span className="text-muted-fg">/{r.max}</span>
                    </span>
                  </div>
                  <div className="h-1 bg-app border border-subtle rounded-sm overflow-hidden">
                    <div
                      className={
                        "h-full transition-all " +
                        (r.earned >= r.max * 0.7 ? "bg-pos" : r.earned > 0 ? "bg-mod" : "bg-subtle")
                      }
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-subtle text-[9px] font-mono uppercase tracking-widest text-muted-fg leading-relaxed">
            Higher score = more institutional-grade signals stacked.
          </div>
        </motion.div>
      )}
    </div>
  );
}

/**
 * Power Breakouts — top 5 highest-conviction momentum picks per day.
 *
 * Data source: /api/power-breakouts (filters the persisted Strongest pool
 * with stricter conviction thresholds). Auto-renews whenever the underlying
 * Strongest scan runs (hourly + 16:00 IST EOD).
 *
 * Premium gating: free users see 1 preview card + upgrade CTA; premium
 * (or admin) users see all 5 picks.
 */
export default function PowerBreakoutsView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      setErr("");
      const { data } = await api.get("/power-breakouts");
      setData(data);
    } catch (e) {
      console.error("/power-breakouts load", e);
      setErr(e?.response?.data?.detail || "Failed to load power breakouts");
      setData({ items: [], count: 0, is_premium_view: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const isPremium = !!data?.is_premium_view;

  return (
    <div className="px-6 py-6" data-testid="power-breakouts-view">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-base font-mono uppercase tracking-widest text-primary-fg flex items-center gap-2">
            <ThumbsUp size={14} weight="fill" className="text-pos" />
            Power Breakouts
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos/40 bg-pos-soft/30 text-pos rounded-sm">
              <Sparkle size={9} weight="fill" className="inline mr-0.5" />
              Premium
            </span>
          </h2>
          <p className="text-xs text-muted-fg max-w-3xl leading-relaxed mt-1">
            Top 5 highest-conviction momentum breakouts today. Hard filters:
            volume ≥ 2× avg, ADX ≥ 25, RS vs Nifty ≥ 5%, tech score ≥ 6.5/10,
            within 8% of 52w high, sector ≥ Nifty. Conviction score combines
            tech, volume, ADX, RS, fresh breakout bonus, sector strength,
            delivery %. Auto-renews when the Strongest scan runs (hourly +
            16:00 IST EOD).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="power-refresh-btn"
            className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-subtle text-secondary-fg hover:text-primary-fg hover:border-pos/40 disabled:opacity-50"
          >
            <ArrowsClockwise size={11} className="inline mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="px-3 py-2 mb-3 border border-neg/40 bg-neg-soft/30 text-neg text-xs font-mono rounded-sm">
          {err}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-fg font-mono text-sm" data-testid="power-loading">
          Loading high-conviction picks…
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 border border-subtle rounded-sm bg-surface text-center" data-testid="power-empty">
          <ThumbsUp size={24} className="mx-auto text-muted-fg mb-3" />
          <div className="text-primary-fg font-medium mb-1">
            No high-conviction breakouts today
          </div>
          <div className="text-sm text-muted-fg max-w-md mx-auto">
            All conviction filters (volume, ADX, RS, tech score, 52w
            proximity, sector strength) must align. In weak market regimes
            this list may be empty. Will refresh on next Strongest scan.
          </div>
        </div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="power-list-wrap"
          >
            <StockList
              items={items}
              onOpen={(it) => onOpenStock?.({ ...it, power_mode: true })}
              onWatch={onWatch}
              watchedSet={watchedSet}
              testIdPrefix="power-row"
              testid="power-list"
              rowProps={(it) => ({
                iconKind: "power",
                rank: it.rank,
                scoreKey: "momentum_score",
                scoreLabel: "Score",
                rightHint:
                  it.conviction_score != null
                    ? `★ ${it.conviction_score}`
                    : undefined,
              })}
            />
          </motion.div>

          {!isPremium && (
            <div
              className="mt-6 p-5 border border-pos/40 bg-pos-soft/20 rounded-sm flex flex-col md:flex-row items-start md:items-center gap-4 justify-between"
              data-testid="power-upgrade-banner"
            >
              <div className="flex items-start gap-3">
                <LockKey size={20} weight="fill" className="text-pos shrink-0 mt-1" />
                <div>
                  <div className="text-primary-fg font-medium mb-1">
                    Showing 1 of 5 picks · Upgrade to unlock the full daily list
                  </div>
                  <div className="text-xs text-muted-fg max-w-md">
                    Premium members see all 5 high-conviction breakouts daily with full
                    Entry / SL / Target and AI fundamental verdicts. Cancel anytime.
                  </div>
                </div>
              </div>
              <Link
                to="/pricing"
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-pos bg-pos text-app rounded-sm hover:bg-pos/90 transition shrink-0"
                data-testid="power-upgrade-cta"
              >
                Unlock — ₹5 trial
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
